import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Phone, Menu, X, ChevronDown, ShoppingBag, User } from "lucide-react";
import { SITE } from "@/lib/site";
import { useGo } from "@/lib/nav";
import { useCart } from "@/lib/cart";
import { PatternLayer, patternFor } from "./patterns";
import { ShippingBanner } from "./ShippingBanner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const links = [
  { label: "Sell Your Gold", href: "/sell" },
  { label: "Collections", href: "/collections" },
  { label: "Catalog", href: "/catalog" },
  { label: "Lab Diamonds", href: "/lab-grown-diamonds" },
  { label: "Our Promise", href: "/our-promise" },
  { label: "Showrooms", href: "/showrooms" },
  { label: "Contact", href: "/contact" },
];

const catalogFlyout = [
  {
    label: "Chains",
    href: "/catalog?category=Chains",
    subs: [
      { label: "Rope Chains", href: "/collections/rope-chains" },
      { label: "Cuban Link", href: "/collections/cuban-link-chains" },
      { label: "Figaro", href: "/collections/figaro-chains" },
      { label: "Franco", href: "/collections/franco-chains" },
      { label: "Box", href: "/collections/box-chains" },
    ],
  },
  {
    label: "Earrings",
    href: "/catalog?category=Earrings",
    subs: [
      { label: "Hoop Earrings", href: "/collections/hoop-earrings" },
      { label: "Stud Earrings", href: "/collections/stud-earrings" },
      { label: "Drop & Dangle", href: "/collections/drop-dangle-earrings" },
    ],
  },
  {
    label: "Rings",
    href: "/catalog?category=Rings",
    subs: [
      { label: "Wedding Bands", href: "/collections/wedding-bands" },
      { label: "Men's Rings", href: "/collections/mens-rings" },
      { label: "Diamond Rings", href: "/collections/diamond-rings" },
    ],
  },
  {
    label: "Bracelets",
    href: "/catalog?category=Bracelets",
    subs: [
      { label: "Rope Bracelets", href: "/collections/rope-bracelets" },
      { label: "Cuban Bracelets", href: "/collections/cuban-bracelets" },
      { label: "Bangles", href: "/collections/bangles" },
    ],
  },
];

const goldFmt = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const UtilityStrip = () => {
  const [oz, setOz] = useState(null);
  const { pathname } = useLocation();
  // P1.15: the live gold rate helps SELLING gold, not shopping for it — show
  // it on the homepage and gold-selling pages only; keep it off shopping
  // flows (catalog/product/cart) where it distracts from purchasing.
  const showGold = ["/", "/sell", "/sell-gold", "/sell-diamonds"].includes(pathname);

  useEffect(() => {
    if (!showGold) return;
    let alive = true;
    const load = () =>
      axios
        .get(`${API}/gold-price`)
        .then((r) => {
          if (alive) setOz(r.data.price_per_troy_ounce);
        })
        .catch(() => {});
    load();
    const timer = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [showGold]);

  return (
    <div className="h-9 bg-[#0A0A0A] border-b border-[#C9A227]/20 flex items-center" data-testid="utility-strip">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full flex items-center justify-between">
        {showGold ? (
          <span
            data-testid="utility-gold-price"
            className="inline-flex items-center gap-2 text-[11px] font-medium tracking-wide text-[#E8CC6E]"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {oz ? `Gold ${goldFmt(oz)}/oz` : "Live gold rate"}
          </span>
        ) : (
          <span
            className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 inline-flex items-center"
            data-testid="utility-pedigree"
          >
            Family owned · 3 Texas showrooms
          </span>
        )}
        <span
          className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 hidden sm:inline-flex items-center"
          data-testid="utility-pedigree-desktop"
        >
          {showGold ? "Family owned · 3 Texas showrooms" : "Free insured US shipping"}
        </span>
      </div>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const go = useGo();
  const cart = useCart();

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      <UtilityStrip />
      <div
        className="border-b border-[#C9A227]/25 shadow-[0_8px_30px_rgb(0,0,0,0.25)] relative"
        style={{
          background:
            "radial-gradient(ellipse 60% 130% at 12% 0%, rgba(201,162,39,0.10), transparent 60%), linear-gradient(180deg, #17130B 0%, #0D0D0D 65%)",
        }}
      >
        <PatternLayer pattern={patternFor("header")} boost />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-20 flex items-center">
          <a href="/" data-testid="header-logo" onClick={go("/")} className="inline-flex items-center shrink min-w-0 lg:shrink-0 lg:mr-6">
            <img src="/assets/logo.png" alt="Gold Time — Fine Jewelry and Watches" className="h-10 w-auto max-w-full min-[450px]:max-w-none object-contain object-left" />
          </a>

          <nav className="hidden lg:flex flex-1 items-center justify-center gap-7 pl-10" data-testid="header-nav">
            {links.map((l) =>
              l.label === "Catalog" ? (
                <div key={l.href} className="relative group" data-testid="nav-catalog-flyout">
                  <a
                    href={l.href}
                    onClick={go(l.href)}
                    data-testid={`nav-link-${l.href.slice(1)}`}
                    className="inline-flex items-center gap-1 text-sm tracking-wide text-neutral-300 hover:text-[#E8CC6E] transition-colors duration-300 whitespace-nowrap"
                  >
                    {l.label}
                    <ChevronDown size={13} strokeWidth={1.5} className="transition-transform duration-300 group-hover:rotate-180" />
                  </a>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-5 invisible opacity-0 translate-y-2 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform,visibility] duration-300 z-50">
                    <div className="bg-[#0D0D0D] border border-[#C9A227]/25 rounded-sm shadow-[0_30px_60px_rgba(0,0,0,0.6)] p-6 grid grid-cols-4 gap-8 w-[640px]">
                      {catalogFlyout.map((col) => (
                        <div key={col.label}>
                          <a
                            href={col.href}
                            onClick={go(col.href)}
                            data-testid={`nav-flyout-cat-${col.label.toLowerCase()}`}
                            className="block text-[11px] uppercase tracking-[0.2em] text-[#E8CC6E] font-semibold hover:text-[#F0D988] transition-colors duration-300 pb-2 border-b border-[#C9A227]/20"
                          >
                            {col.label}
                          </a>
                          <div className="mt-3 flex flex-col gap-2">
                            {col.subs.map((s) => (
                              <a
                                key={s.href}
                                href={s.href}
                                onClick={go(s.href)}
                                data-testid={`nav-flyout-sub-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className="text-sm text-neutral-400 hover:text-[#E8CC6E] transition-colors duration-200"
                              >
                                {s.label}
                              </a>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={go(l.href)}
                  data-testid={`nav-link-${l.href.slice(1)}`}
                  className="text-sm tracking-wide text-neutral-300 hover:text-[#E8CC6E] transition-colors duration-300 whitespace-nowrap"
                >
                  {l.label}
                </a>
              )
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <Link
              to="/account"
              data-testid="header-account"
              className="w-11 h-11 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-neutral-300 hover:text-[#E8CC6E] hover:border-[#E8CC6E] hover:shadow-[0_0_16px_rgba(212,175,55,0.25)] transition-[color,border-color,box-shadow] duration-300"
              aria-label="Account"
            >
              <User size={19} strokeWidth={1.5} />
            </Link>
            <Link
              to="/cart"
              data-testid="header-cart"
              className="relative w-11 h-11 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-neutral-300 hover:text-[#E8CC6E] hover:border-[#E8CC6E] hover:shadow-[0_0_16px_rgba(212,175,55,0.25)] transition-[color,border-color,box-shadow] duration-300"
              aria-label="Cart"
            >
              <ShoppingBag size={19} strokeWidth={1.5} />
              {cart.count > 0 && (
                <span
                  data-testid="header-cart-count"
                  className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center"
                >
                  {cart.count}
                </span>
              )}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-call-button"
                  aria-label="Call a Showroom"
                  className="w-11 h-11 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 flex items-center justify-center hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_0_20px_rgba(212,175,55,0.25)]"
                >
                  <Phone size={18} strokeWidth={1.75} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0D0D0D] border border-[#C9A227]/25 rounded-sm p-2 min-w-[280px]">
                {SITE.locations.map((loc, i) => (
                  <DropdownMenuItem key={loc.name} asChild className="focus:bg-[#1A1A1A] rounded-sm cursor-pointer">
                    <a href={loc.phoneHref} data-testid={`header-call-location-${i}`} className="flex items-center justify-between gap-6 px-3 py-2.5">
                      <span className="text-sm text-neutral-300">{loc.name}</span>
                      <span className="text-sm font-semibold text-[#E8CC6E]">{loc.phone}</span>
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="lg:hidden flex items-center gap-2 ml-auto">
            <Link to="/account" data-testid="header-account-mobile" className="w-11 h-11 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-neutral-300" aria-label="Account">
              <User size={18} strokeWidth={1.5} />
            </Link>
            <Link to="/cart" data-testid="header-cart-mobile" className="relative w-11 h-11 rounded-full border border-[#C9A227]/40 flex items-center justify-center text-neutral-300" aria-label="Cart">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {cart.count > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center">
                  {cart.count}
                </span>
              )}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-call-mobile"
                  aria-label="Call a Showroom"
                  className="w-11 h-11 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 flex items-center justify-center"
                >
                  <Phone size={16} strokeWidth={1.75} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-[#0D0D0D] border border-[#C9A227]/25 rounded-sm p-2 min-w-[280px]">
                {SITE.locations.map((loc, i) => (
                  <DropdownMenuItem key={loc.name} asChild className="focus:bg-[#1A1A1A] rounded-sm cursor-pointer">
                    <a href={loc.phoneHref} data-testid={`header-call-location-mobile-${i}`} className="flex items-center justify-between gap-6 px-3 py-2.5">
                      <span className="text-sm text-neutral-300">{loc.name}</span>
                      <span className="text-sm font-semibold text-[#E8CC6E]">{loc.phone}</span>
                    </a>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <button
              className="w-11 h-11 flex items-center justify-center text-white"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden bg-[#0D0D0D] border-t border-[#C9A227]/25 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => {
                  go(l.href)(e);
                  setOpen(false);
                }}
                data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                className="text-base text-neutral-300"
              >
                {l.label}
              </a>
            ))}
            <div className="flex flex-col gap-2">
              {SITE.locations.map((loc, i) => (
                <a
                  key={loc.name}
                  href={loc.phoneHref}
                  data-testid={`mobile-call-location-${i}`}
                  className="inline-flex items-center gap-2 text-[#E8CC6E] font-semibold"
                >
                  <Phone size={16} strokeWidth={1.5} /> {loc.name}: {loc.phone}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
      <ShippingBanner />
    </header>
  );
};
